import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './features/auth/hooks/use-auth';
import LoginPage from './routes/LoginPage';
import AuthenticatedLayout from './routes/AuthenticatedLayout';
import Dashboard from './routes/Dashboard';
import UsersList from './routes/UsersList';
import AdminsList from './routes/AdminsList';
import ApplicationsList from './routes/ApplicationsList';
import PlansList from './routes/PlansList';
import UserOperationsList from './routes/UserOperationsList';
import UserOperationDetail from './routes/UserOperationDetail';
import InitialSetupPage from './routes/InitialSetupPage';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (redirect to dashboard if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      <Route
        path="/setup"
        element={
          <PublicRoute>
            <InitialSetupPage />
          </PublicRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
      </Route>

      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<UsersList />} />
      </Route>

      <Route
        path="/admins"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminsList />} />
      </Route>

      <Route
        path="/applications"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ApplicationsList />} />
      </Route>

      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PlansList />} />
      </Route>

      <Route
        path="/user-operations"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<UserOperationsList />} />
        <Route path=":traceId" element={<UserOperationDetail />} />
      </Route>
    </Routes>
  );
}

export default App;
